import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Home as HomeIcon,
  Policy as PolicyIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  ContactSupport as ContactSupportIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as AccessTimeIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import './RefundPolicy.css';

const RefundPolicy = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(false);

  const handleFAQChange = (panel) => (event, isExpanded) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  const handleDownloadPDF = () => {
    // Mock action pour télécharger le PDF
    alert('Téléchargement de la politique de remboursement en cours...');
  };

  const faqData = [
    {
      id: 'faq1',
      question: 'Comment demander un remboursement ?',
      answer: 'Pour demander un remboursement, connectez-vous à votre compte Mission Sport, accédez à la section "Mes réservations" et cliquez sur "Demander un remboursement" pour la mission concernée. Vous pouvez également nous contacter directement via notre formulaire de support.'
    },
    {
      id: 'faq2',
      question: 'Combien de temps pour recevoir le remboursement ?',
      answer: 'Les remboursements sont traités sous 5 à 7 jours ouvrés après validation de votre demande. Le délai de réception dépend de votre banque et du moyen de paiement utilisé (carte bancaire, PayPal, etc.).'
    },
    {
      id: 'faq3',
      question: 'Quels moyens de paiement sont remboursés ?',
      answer: 'Tous les paiements effectués via Stripe (cartes bancaires) et PayPal peuvent être remboursés. Le remboursement s\'effectue automatiquement sur le même moyen de paiement utilisé lors de la transaction initiale.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Politique de remboursement | Mission Sport</title>
        <meta 
          name="description" 
          content="Découvrez notre politique de remboursement transparente pour les organisations sportives et professionnels. Conditions, délais et procédures de remboursement sur Mission Sport." 
        />
        <meta name="keywords" content="remboursement sport, politique annulation, Mission Sport, remboursement mission sportive, annulation réservation" />
        <meta property="og:title" content="Politique de remboursement | Mission Sport" />
        <meta 
          property="og:description" 
          content="Politique de remboursement transparente pour les missions sportives. Conditions et procédures pour organisations et professionnels." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://missionsport.com/refund-policy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Politique de remboursement | Mission Sport" />
        <meta 
          name="twitter:description" 
          content="Politique de remboursement transparente pour les missions sportives. Conditions et procédures pour organisations et professionnels." 
        />
      </Helmet>

      <div className="refund-policy">
        {/* Header Section */}
        <Box className="refund-header" sx={{ bgcolor: 'primary.main', color: 'white', py: 6 }}>
          <Container maxWidth="lg">
            {/* Breadcrumbs */}
            <Breadcrumbs 
              aria-label="breadcrumb" 
              sx={{ mb: 3, color: 'white' }}
              separator={<Typography sx={{ color: 'white', opacity: 0.7 }}>›</Typography>}
            >
              <Link 
                component={RouterLink} 
                to="/" 
                sx={{ 
                  color: 'white', 
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                Accueil
              </Link>
              <Link 
                component={RouterLink} 
                to="/terms" 
                sx={{ 
                  color: 'white', 
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                CGU
              </Link>
              <Typography sx={{ color: 'white', fontWeight: 500 }}>
                Politique de remboursement
              </Typography>
            </Breadcrumbs>

            <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
              <PolicyIcon sx={{ fontSize: 64, mb: 2, opacity: 0.9 }} />
              <Typography 
                variant="h1" 
                component="h1" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Politique de remboursement
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3, 
                  opacity: 0.9,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  lineHeight: 1.4
                }}
              >
                Conditions transparentes pour les remboursements sur Mission Sport
              </Typography>
              
              <Button 
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{ 
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'white'
                  }
                }}
              >
                Télécharger en PDF
              </Button>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 6 }}>
          {/* Introduction */}
          <section className="intro-section">
            <Paper elevation={2} sx={{ p: 4, mb: 6, borderRadius: 3 }}>
              <Typography 
                variant="h4" 
                component="h2" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  color: 'primary.main',
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                Notre engagement
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2, 
                  lineHeight: 1.7,
                  fontSize: '1.1rem'
                }}
              >
                Mission Sport s'engage à mettre en relation les organisations sportives avec des professionnels qualifiés 
                dans un environnement de confiance. Dans cette optique, nous avons établi une politique de remboursement 
                claire et équitable pour toutes les parties.
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: 1.7,
                  fontSize: '1.1rem',
                  color: 'text.secondary'
                }}
              >
                Cette politique vise à protéger les intérêts des organisations et des professionnels tout en maintenant 
                la qualité de service qui fait notre réputation.
              </Typography>
            </Paper>
          </section>

          {/* Conditions générales */}
          <section className="general-conditions">
            <Card elevation={1} sx={{ mb: 4, borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mr: 2 }} />
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    Conditions générales de remboursement
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Délais d'annulation
                    </Typography>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <AccessTimeIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Plus de 48h avant la mission"
                          secondary="Remboursement intégral (100%)"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <AccessTimeIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Entre 24h et 48h avant"
                          secondary="Remboursement partiel (50%)"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Moins de 24h avant"
                          secondary="Aucun remboursement"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Cas de remboursement acceptés
                    </Typography>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Annulation par l'organisation" />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Non-présentation du professionnel" />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Modification substantielle de la mission" />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Problème technique de notre plateforme" />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </section>

          {/* Remboursement organisations */}
          <section className="organizations-refund">
            <Card elevation={1} sx={{ mb: 4, borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <BusinessIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    Remboursement pour les organisations sportives
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Les organisations sportives peuvent demander un remboursement dans les cas suivants :
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
                      ✓ Remboursement intégral
                    </Typography>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Non-présentation du professionnel"
                          secondary="Sans justification valable"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Compétences non conformes"
                          secondary="Professionnel ne correspondant pas au profil demandé"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Annulation dans les délais"
                          secondary="Plus de 48h avant la mission"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'warning.main' }}>
                      ⚠ Remboursement partiel
                    </Typography>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Annulation tardive"
                          secondary="Entre 24h et 48h avant la mission"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Modification de dernière minute"
                          secondary="Changement d'horaires ou de lieu"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </section>

          {/* Remboursement professionnels */}
          <section className="professionals-refund">
            <Card elevation={1} sx={{ mb: 4, borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PersonIcon sx={{ fontSize: 32, color: 'secondary.main', mr: 2 }} />
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    Remboursement pour les professionnels
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Les professionnels peuvent demander un remboursement des frais de service dans ces situations :
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
                      ✓ Remboursement des frais
                    </Typography>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Annulation par l'organisation"
                          secondary="Quelle que soit la raison"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Modification substantielle"
                          secondary="Changement majeur des conditions de la mission"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Problème de paiement"
                          secondary="Défaut de paiement de l'organisation"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'info.main' }}>
                      ℹ Compensation possible
                    </Typography>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Frais de déplacement engagés"
                          secondary="Si annulation tardive par l'organisation"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Temps de préparation"
                          secondary="Pour les missions spécialisées"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </section>

          {/* Délais et modes de remboursement */}
          <section className="refund-process">
            <Card elevation={1} sx={{ mb: 4, borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PaymentIcon sx={{ fontSize: 32, color: 'info.main', mr: 2 }} />
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    Délais et modes de remboursement
                  </Typography>
                </Box>
                
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Délais de traitement
                    </Typography>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <ScheduleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Validation de la demande"
                          secondary="2 à 3 jours ouvrés"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <ScheduleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Traitement du remboursement"
                          secondary="5 à 7 jours ouvrés"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <ScheduleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Réception sur votre compte"
                          secondary="Selon votre banque (1 à 3 jours)"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'secondary.main' }}>
                      Moyens de paiement
                    </Typography>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <MoneyIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Stripe (Cartes bancaires)"
                          secondary="Remboursement automatique sur la carte utilisée"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <MoneyIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="PayPal"
                          secondary="Remboursement sur le compte PayPal"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <MoneyIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Virement bancaire"
                          secondary="En cas d'impossibilité technique"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </section>

          {/* Procédure */}
          <section className="procedure">
            <Card elevation={1} sx={{ mb: 4, borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <ContactSupportIcon sx={{ fontSize: 32, color: 'warning.main', mr: 2 }} />
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    Procédure à suivre
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Pour demander un remboursement, suivez ces étapes simples :
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography 
                        variant="h2" 
                        sx={{ 
                          color: 'primary.main', 
                          fontWeight: 700, 
                          mb: 1,
                          fontSize: '3rem'
                        }}
                      >
                        1
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        Connexion
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Connectez-vous à votre compte Mission Sport et accédez à "Mes réservations"
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography 
                        variant="h2" 
                        sx={{ 
                          color: 'secondary.main', 
                          fontWeight: 700, 
                          mb: 1,
                          fontSize: '3rem'
                        }}
                      >
                        2
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        Demande
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cliquez sur "Demander un remboursement" et remplissez le formulaire
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography 
                        variant="h2" 
                        sx={{ 
                          color: 'success.main', 
                          fontWeight: 700, 
                          mb: 1,
                          fontSize: '3rem'
                        }}
                      >
                        3
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        Validation
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Notre équipe valide votre demande et procède au remboursement
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </section>

          {/* Exceptions */}
          <section className="exceptions">
            <Card elevation={1} sx={{ mb: 4, borderRadius: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CancelIcon sx={{ fontSize: 32, color: 'error.main', mr: 2 }} />
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      color: 'error.main'
                    }}
                  >
                    Exceptions - Cas non remboursables
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7, color: 'text.primary' }}>
                  Les remboursements ne sont pas accordés dans les situations suivantes :
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Non-respect des conditions"
                          secondary="Violation des termes d'utilisation"
                          sx={{ color: 'text.primary' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Retard du professionnel"
                          secondary="Sans justification valable"
                          sx={{ color: 'text.primary' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Fraude ou fausses informations"
                          secondary="Tentative de tromperie"
                          sx={{ color: 'text.primary' }}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <List>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Annulation tardive"
                          secondary="Moins de 24h avant la mission"
                          sx={{ color: 'text.primary' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Mission déjà réalisée"
                          secondary="Demande après exécution"
                          sx={{ color: 'text.primary' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Cas de force majeure"
                          secondary="Événements indépendants de notre volonté"
                          sx={{ color: 'text.primary' }}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </section>

          <Divider sx={{ my: 6 }} />

          {/* FAQ Section */}
          <section className="faq-section">
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 1,
                fontWeight: 600,
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              Questions fréquentes
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center', 
                mb: 4, 
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Trouvez rapidement les réponses à vos questions sur les remboursements
            </Typography>
            
            {faqData.map((faq) => (
              <Accordion 
                key={faq.id}
                expanded={expandedFAQ === faq.id}
                onChange={handleFAQChange(faq.id)}
                sx={{ 
                  mb: 2, 
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 1
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    '&.Mui-expanded': {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </section>

          <Divider sx={{ my: 6 }} />

          {/* Contact Support */}
          <section className="contact-support">
            <Paper 
              elevation={2}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
              }}
            >
              <ContactSupportIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography 
                variant="h4" 
                component="h2" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                Besoin d'aide pour un remboursement ?
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                  color: 'text.secondary',
                  maxWidth: 500,
                  mx: 'auto',
                  lineHeight: 1.6
                }}
              >
                Notre équipe support est disponible pour vous accompagner dans vos demandes de remboursement. 
                Contactez-nous pour une assistance personnalisée.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  size="large"
                  component={RouterLink}
                  to="/contact"
                  startIcon={<ContactSupportIcon />}
                  sx={{ 
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  Contacter le support
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  component={RouterLink}
                  to="/aide"
                  sx={{ 
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  Centre d'aide
                </Button>
              </Box>
              
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Email direct : <strong>remboursement@missionsport.com</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Temps de réponse moyen : 24h
                </Typography>
              </Box>
            </Paper>
          </section>
        </Container>
      </div>
    </>
  );
};

export default RefundPolicy;